import { useSelector } from "react-redux";
import { getAppConfig } from "../../../../../../ducks/appConfig";
import {
  circleCreateSymbol,
  DEFAULT_CREATE_CIRCLE_OPTIONS,
  ShadowedCircleSymbol,
} from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedCircleSymbol";
import {
  DEFAULT_EDITOR_POINT_OPTIONS,
  editorPointSymbol,
  ShadowedPointSymbol,
} from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";
import {
  DEFAULT_EDITOR_POLYGON_OPTIONS,
  DEFAULT_EDITOR_POLYLINE_OPTIONS,
  polygonCreateSymbol,
  polygonEditSymbol,
  polygonPointCreateSymbol,
  polylineCreateSymbol,
  polylineEditSymbol,
  ShadowedPolySymbol,
} from "../../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";

export enum GeometrySelectionSymbolType {
  point,
  pointPolygon,
  polygonEdit,
  polygonCreate,
  polylineEdit,
  polylineCreate,
  circleCreate,
}

const symbolMapOptions = {
  [GeometrySelectionSymbolType.point]: DEFAULT_EDITOR_POINT_OPTIONS,
  [GeometrySelectionSymbolType.pointPolygon]: DEFAULT_EDITOR_POINT_OPTIONS,
  [GeometrySelectionSymbolType.polygonEdit]: DEFAULT_EDITOR_POLYGON_OPTIONS,
  [GeometrySelectionSymbolType.polygonCreate]: { ...DEFAULT_EDITOR_POLYGON_OPTIONS, showVertex: false },
  [GeometrySelectionSymbolType.polylineEdit]: DEFAULT_EDITOR_POLYLINE_OPTIONS,
  [GeometrySelectionSymbolType.polylineCreate]: { ...DEFAULT_EDITOR_POLYLINE_OPTIONS, showVertex: false },
  [GeometrySelectionSymbolType.circleCreate]: DEFAULT_CREATE_CIRCLE_OPTIONS,
} as Record<GeometrySelectionSymbolType, unknown>;

const symbolMapType = {
  [GeometrySelectionSymbolType.point]: ShadowedPointSymbol,
  [GeometrySelectionSymbolType.pointPolygon]: ShadowedPolySymbol,
  [GeometrySelectionSymbolType.polygonEdit]: ShadowedPolySymbol,
  [GeometrySelectionSymbolType.polygonCreate]: ShadowedPolySymbol,
  [GeometrySelectionSymbolType.polylineEdit]: ShadowedPolySymbol,
  [GeometrySelectionSymbolType.polylineCreate]: ShadowedPolySymbol,
  [GeometrySelectionSymbolType.circleCreate]: ShadowedCircleSymbol,
} as Record<
  GeometrySelectionSymbolType,
  typeof ShadowedPointSymbol | typeof ShadowedPolySymbol | typeof ShadowedCircleSymbol
>;

const symbolMap = {
  [GeometrySelectionSymbolType.point]: editorPointSymbol,
  [GeometrySelectionSymbolType.pointPolygon]: polygonPointCreateSymbol,
  [GeometrySelectionSymbolType.polygonEdit]: polygonEditSymbol,
  [GeometrySelectionSymbolType.polygonCreate]: polygonCreateSymbol,
  [GeometrySelectionSymbolType.polylineEdit]: polylineEditSymbol,
  [GeometrySelectionSymbolType.polylineCreate]: polylineCreateSymbol,
  [GeometrySelectionSymbolType.circleCreate]: circleCreateSymbol,
} as Record<GeometrySelectionSymbolType, ShadowedPointSymbol | ShadowedPolySymbol | ShadowedCircleSymbol>;

export function useSelectionSymbols(): Record<
  GeometrySelectionSymbolType,
  ShadowedPointSymbol | ShadowedPolySymbol | ShadowedCircleSymbol
> {
  const appConfig = useSelector(getAppConfig);
  const featureSymbolParameters = appConfig?.settings?.symbols?.featureSymbolParameters;
  const symbols = symbolMap;

  if (featureSymbolParameters) {
    Object.entries(symbolMapType).forEach(([type, SymbolConstructor]) => {
      symbols[type] = new SymbolConstructor({
        ...symbolMapOptions[type],
        ...featureSymbolParameters,
      });
    });
  }

  return symbols;
}
